import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAskDesk, useDeskQuestionQuery } from '../hooks/useDesk';

type Props = {
  question: string;
  context: string[];
  questionId?: string | undefined;
  onAsked?: ((questionId: string) => void) | undefined;
};

/** From a refusal: hand the question and the chat so far to the research desk, then show the analyst's reply
 * here when it comes (Teams analysts and the portal's Desk questions page answer the same queue). The chat keeps
 * the question's id, so the reply still appears after a reload or when the chat is reopened. */
export function AskAnalyst({ question, context, questionId, onAsked }: Props) {
  const ask = useAskDesk();
  const asked = useDeskQuestionQuery(questionId ?? null);

  if (!questionId) {
    return (
      <Button
        size="small"
        variant="outlined"
        startIcon={<SupportAgentOutlined />}
        disabled={ask.isPending}
        onClick={() => { ask.mutate({ question, context }, { onSuccess: (q) => { onAsked?.(q.id); } }); }}
        sx={{ alignSelf: 'flex-start' }}
      >
        {ask.isError ? 'Could not send. Try again' : 'Ask an analyst'}
      </Button>
    );
  }
  const q = asked.data;
  if (q?.status === 'answered') {
    return (
      <Alert severity="success" variant="outlined" icon={<SupportAgentOutlined />}>
        <Typography variant="subtitle2">Answer from the research desk ({q.answeredBy})</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>{q.answer}</Typography>
      </Alert>
    );
  }
  return (
    <Typography variant="body2" role="status" sx={{ color: 'text.secondary' }}>
      Sent to the research desk as {questionId}. An analyst&apos;s reply will appear here.
    </Typography>
  );
}
